import React from "react"

import { CodeBlock } from "@javascript/components/ui/code-block"
import {
  TypographyH2,
  TypographyH3,
  TypographyInlineCode,
  TypographyList,
  TypographyP
} from "@javascript/components/ui/typography"

export function OverviewPage() {
  const themeManifestExample = `{
  "name": "Elegant",
  "version": "3.0.0",
  "contract_version": 3,
  "blocks": [
    { "id": "hero", "type": "hero" },
    { "id": "schedule", "type": "event_schedule" },
    { "id": "gallery", "type": "gallery" },
    { "id": "rsvp", "type": "rsvp" }
  ],
  "runtime": {
    "entrypoint": "assets/theme.js",
    "dependencies": ["jquery-3"]
  }
}`

  const templateLiquidExample = `<main class="invite">
  {% block "hero" %}
  {% block "schedule" %}
  {% block "gallery" %}
  {% block "rsvp" %}
</main>`

  const contentStructureExample = `{
  "version": 2,
  "couple": {
    "partners": [
      { "id": "partner-1", "name": "Aulia" },
      { "id": "partner-2", "name": "Rizki" }
    ]
  },
  "events": [
    {
      "id": "ceremony",
      "title": "Ceremony",
      "starts_at": "2026-11-20T16:00",
      "timezone": "Asia/Jakarta",
      "venue": { "name": "Plataran" }
    }
  ]
}`

  return (
    <div className="space-y-8">
      <div>
        <TypographyH2>The Render Model</TypographyH2>
        <TypographyP>
          Every invitation is rendered by composing a reusable <TypographyInlineCode>theme</TypographyInlineCode> with an invitation-owned content document.
        </TypographyP>
        <TypographyP className="font-mono text-sm">
          rendered invitation = theme (layout + block slots + optional runtime) × canonical invitation content
        </TypographyP>
        <TypographyP>
          The designer controls layout, styling, and trusted presentational behavior. The platform owns wedding facts,
          validation, block rendering, assets, RSVP persistence, and security-sensitive workflows.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Themes are packages on disk</TypographyH3>
        <TypographyP>A curated theme is a folder under <TypographyInlineCode>themes/&lt;key&gt;/</TypographyInlineCode> containing:</TypographyP>
        <TypographyList>
          <li><TypographyInlineCode>theme.json</TypographyInlineCode> — metadata and platform block instances</li>
          <li><TypographyInlineCode>template.liquid</TypographyInlineCode> — free-form layout with block slots</li>
          <li><TypographyInlineCode>assets/style.css</TypographyInlineCode> and catalog preview assets</li>
          <li><TypographyInlineCode>assets/theme.js</TypographyInlineCode> — optional trusted runtime for contract v3</li>
        </TypographyList>
      </div>

      <div>
        <TypographyH3>Theme manifest: <TypographyInlineCode>theme.json</TypographyInlineCode></TypographyH3>
        <TypographyP>Each block has a template-local ID and a platform-defined type:</TypographyP>
        <CodeBlock code={themeManifestExample} language="json" className="my-4" />
      </div>

      <div>
        <TypographyH3>Designer layout: <TypographyInlineCode>template.liquid</TypographyInlineCode></TypographyH3>
        <TypographyP>The template places those IDs. It cannot read invitation data directly.</TypographyP>
        <CodeBlock code={templateLiquidExample} language="liquid" className="my-4" />
      </div>

      <div>
        <TypographyH3>Invitation content structure</TypographyH3>
        <TypographyP>
          Content is versioned and organized by durable wedding concepts—not by the current theme or its block IDs. Switching themes never moves or deletes the couple's facts.
        </TypographyP>
        <CodeBlock code={contentStructureExample} language="json" className="my-4" />
      </div>

      <div>
        <TypographyH3>Platform defines, themes use</TypographyH3>
        <TypographyP>
          The platform defines block types and gives every renderer the same canonical context. Themes choose which
          capabilities to place; they never define block types, persistence, form fields, or security-sensitive workflows.
        </TypographyP>
        <TypographyP>
          A curated contract v3 theme may declare one optional runtime and platform-allowlisted dependencies. That
          runtime is reviewed as trusted application code; unrestricted theme uploads and arbitrary package URLs are
          not part of this model.
        </TypographyP>
      </div>
    </div>
  )
}
